/**
 * Mission select (tasks 9.5, 14.3).
 *
 * Every word on this screen comes from the catalog and the save document. The
 * shell holds no mission list, so publishing a mission is a content change.
 *
 * The catalog it renders may have come from this device rather than from the
 * network, and the screen says so: a player looking at a short list offline is
 * owed the difference between "there are no more missions" and "this device has
 * not seen the list lately" (`offline-delivery` spec, "Catalog fetch with
 * offline fallback").
 */
import type { JSX } from 'react';
import type { DifficultyProfiles } from '@managing-up/schema';
import { AsyncBoundary } from '../components/AsyncBoundary.js';
import { ButtonLink } from '../components/Button.js';
import { Card, CardBody } from '../components/Card.js';
import { LaunchLayout, PageHeading } from '../components/LaunchLayout.js';
import {
  isTutorial,
  lockStateOf,
  missionsInOrder,
  type Catalog,
  type CatalogEntry,
} from '../lib/catalog.js';
import type { CatalogOrigin } from '../lib/content/delivery.js';
import { useContentSource } from '../lib/content-context.js';
import {
  completedMissionIds,
  hasCompletedTutorial,
  progressFor,
  type SaveProfile,
} from '../lib/save/types.js';
import { useAsync } from '../hooks/use-async.js';
import { useActiveProfile } from '../store/view-store.js';

function BestResults({
  profiles,
  save,
  missionId,
}: {
  readonly profiles: DifficultyProfiles;
  readonly save: SaveProfile | null;
  readonly missionId: string;
}): JSX.Element {
  const progress = save ? progressFor(save, missionId) : null;
  return (
    <dl
      className="mt-3 grid grid-cols-3 gap-2 text-center"
      data-testid={`mission-results-${missionId}`}
    >
      {profiles.profiles.map((difficulty) => {
        const best = progress?.best[difficulty.id];
        return (
          <div key={difficulty.id} className="rounded-lg bg-surface-muted px-2 py-2">
            <dt className="text-xs font-medium text-ink-subtle">{difficulty.label}</dt>
            <dd
              className="mt-0.5 text-sm font-semibold text-ink"
              data-testid={`best-${missionId}-${difficulty.id}`}
            >
              {best ? (
                <>
                  {best.grade}
                  <span className="ml-1 text-xs font-normal text-ink-muted">
                    {best.score.toLocaleString()}
                  </span>
                </>
              ) : (
                <span className="font-normal text-ink-subtle">Unplayed</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function MissionCard({
  catalog,
  entry,
  profiles,
  save,
}: {
  readonly catalog: Catalog;
  readonly entry: CatalogEntry;
  readonly profiles: DifficultyProfiles;
  readonly save: SaveProfile | null;
}): JSX.Element {
  const completed = save ? completedMissionIds(save) : [];
  const lock = lockStateOf(catalog, entry, completed);
  // A tutorial is never locked, banks nothing and holds no record, so the two
  // things a mission card exists to show — what it costs to unlock and what you
  // scored — are both meaningless on it (`game-shell` spec).
  const tutorial = isTutorial(entry);
  const walked = tutorial && save !== null && hasCompletedTutorial(save, entry.id);

  return (
    <Card data-testid={`mission-${entry.id}`} data-kind={entry.kind}>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-ink">{entry.title}</h2>
            <p className="text-sm text-ink-muted">{entry.company}</p>
            {tutorial ? (
              <p className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className="rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-ink-muted"
                  data-testid={`mission-optional-${entry.id}`}
                >
                  Optional
                </span>
                {walked ? (
                  <span
                    className="rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-ink-muted"
                    data-testid={`mission-tutorial-done-${entry.id}`}
                  >
                    You have played this
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          {lock.locked ? (
            <span
              className="rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-ink-muted"
              data-testid={`mission-locked-${entry.id}`}
            >
              Locked
            </span>
          ) : (
            <ButtonLink variant="primary" to={`/play/${entry.id}`} data-testid={`play-${entry.id}`}>
              Play
            </ButtonLink>
          )}
        </div>

        <p className="mt-3 text-sm text-ink">{entry.premise}</p>

        {lock.locked ? (
          <p className="mt-3 text-sm text-ink-muted" data-testid={`mission-requires-${entry.id}`}>
            {lock.missing.length > 0
              ? `Unlocks when you complete ${lock.missing.map((m) => m.title).join(' and ')}.`
              : 'Unlocks when its earlier missions are published and complete.'}
          </p>
        ) : null}

        {tutorial ? (
          <p className="mt-3 text-sm text-ink-muted" data-testid={`mission-note-${entry.id}`}>
            A walk through the interface, played as a real week. It is not graded, it earns nothing,
            and you can leave it at any point. Skip it and the campaign is unchanged.
          </p>
        ) : (
          <BestResults profiles={profiles} save={save} missionId={entry.id} />
        )}
      </CardBody>
    </Card>
  );
}

/** What a catalog that did not come from the network needs to say. */
function CatalogNotice({ origin }: { readonly origin: CatalogOrigin }): JSX.Element | null {
  if (origin === 'network') return null;
  return (
    <p
      role="status"
      data-testid="catalog-offline"
      data-origin={origin}
      className="mb-4 rounded-lg border border-warning bg-warning-soft px-3 py-2 text-sm text-ink"
    >
      {origin === 'cache'
        ? 'This is the mission list saved on this device. Missions published since your last connection are not shown yet.'
        : 'The mission list did not load. This is the copy already saved on this device. Reconnect to get the rest.'}
    </p>
  );
}

export function MissionSelectScreen(): JSX.Element {
  const source = useContentSource();
  const save = useActiveProfile();
  const catalog = useAsync(() => source.catalog(), [source]);
  const profiles = useAsync(() => source.profiles(), [source]);

  return (
    <LaunchLayout title="Missions">
      <PageHeading
        title="Missions"
        lede="Each one is a working week at a company that has recently had an idea."
      />
      <AsyncBoundary result={catalog} label="the mission catalog">
        {(result) => (
          <>
            {/*
              Outside the difficulty-profile boundary on purpose: a device that
              has never been online has no cached profiles either, and the
              explanation for a short list is the one thing it must still be
              able to say.
            */}
            <CatalogNotice origin={result.origin} />
            {result.catalog.missions.length === 0 ? (
              <p className="text-sm text-ink-muted" data-testid="catalog-empty">
                No missions are on this device yet. Connect once and the whole campaign is saved
                here, ready to play offline.
              </p>
            ) : null}
            <AsyncBoundary result={profiles} label="the difficulty profiles">
              {(profilesValue) => (
                <ul className="flex list-none flex-col gap-4 p-0">
                  {missionsInOrder(result.catalog).map((entry) => (
                    <li key={entry.id}>
                      <MissionCard
                        catalog={result.catalog}
                        entry={entry}
                        profiles={profilesValue}
                        save={save}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </AsyncBoundary>
          </>
        )}
      </AsyncBoundary>
    </LaunchLayout>
  );
}
